import { ROUTE_AVAILABILITY, ROUTE_DASHBOARD_BOOKINGS, ROUTE_DASHBOARD_BOOKING_PAGES } from '@/constants/routes';
import useApplicationConfig from '@/hooks/useApplicationConfig';
import useAuth from '@/hooks/useAuth';
import { useListEventTypesQuery } from '@/queries/eventTypes.queries';
import { useListResourcesQuery } from '@/queries/resources.queries';
import calendarService from '@/services/calendar/calendar.service';
import { Box, Button, Card, CardBody, CardFooter, CardHeader, Grid, Heading, Tag, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FcClock, FcDataSheet, FcLike } from 'react-icons/fc';
import { Link } from 'react-router-dom';

export default function HomePage() {
	const { t } = useTranslation();
	const auth = useAuth();
	const appConfig = useApplicationConfig();
	const availabilityQuery = useListResourcesQuery();
	const eventTypesQuery = useListEventTypesQuery();

	const isFirstStepCompleted = availabilityQuery.data?.resources?.length ? true : false;
	const isSecondStepCompleted = eventTypesQuery.data?.eventTypes?.length ? true : false;

	return (
		<Box px={6} py={2} h="full" overflow="auto">
			<Box
				maxW={{
					base: 'container.md',
					xl: 'container.lg',
					'2xl': 'container.xl',
				}}
				mx="auto"
				pt={4}
			>
				<Box>
					{/* Capitalize first letter */}
					<Text color="gray.500" fontWeight={400} textTransform="capitalize" fontSize="sm" letterSpacing="wide">
						{calendarService.formatDate({
							date: new Date(),
							locale: auth.getCurrentUser().locale,
							options: {
								day: 'numeric',
								weekday: 'long',
								month: 'long',
							},
						})}
					</Text>
					<Heading my={0} fontWeight="800" size="lg">
						{t('Welcome to {{appname}} 👋', { appname: appConfig.name })}
					</Heading>

					<Text color="gray.600" fontWeight={400}>
						{t(
							'Create reservation calendars where people can select the perfect moment for an event without back and forth emails or endless conversations.'
						)}
					</Text>
				</Box>

				<Grid
					pb={4}
					mt={10}
					gap={6}
					gridTemplateColumns={{
						base: 'auto',
						md: 'repeat(1, 1fr)',
						lg: 'repeat(1, 1fr)',
						xl: 'repeat(3, 1fr)',
						'2xl': 'repeat(3, 1fr)',
					}}
				>
					<Step1 isCompleted={isFirstStepCompleted} />
					<Step2 isCompleted={isSecondStepCompleted} />
					<Step3 />
				</Grid>
			</Box>
		</Box>
	);
}

interface IStepProps {
	isCompleted?: boolean;
}

function Step1(props: IStepProps) {
	const { t } = useTranslation();
	return (
		<Card position="relative">
			<Tag
				display={props.isCompleted ? 'flex' : 'none'}
				size="lg"
				position="absolute"
				top={-1}
				left={-8}
				colorScheme="green"
				variant="subtle"
				// Rotate 45deg
				transform="rotate(-20deg)"
				shadow="md"
			>
				{t('COMPLETED')}
			</Tag>
			<CardHeader>
				<Grid gridTemplateColumns="auto 1fr">
					<Box pl={1} pr={4}>
						<FcClock size={60} />
					</Box>
					<Grid alignContent="center">
						<Text fontWeight={800} color="primary.500" fontSize="sm">
							{t('STEP {{number}}', { number: 1 })}
						</Text>
						<Text fontWeight={800} fontSize="xl">
							{t('Set your availability')}
						</Text>
					</Grid>
				</Grid>
			</CardHeader>
			<CardBody>
				<Text>{t('Set your schedule, your availability preferences and connect calendars to avoid conflicts.')}</Text>
			</CardBody>
			<CardFooter>
				<Button to={ROUTE_AVAILABILITY} as={Link} w="full" colorScheme="primary">
					{t('View availability')}
				</Button>
			</CardFooter>
		</Card>
	);
}

function Step2(props: IStepProps) {
	const { t } = useTranslation();
	return (
		<Card position="relative">
			<Tag
				display={props.isCompleted ? 'flex' : 'none'}
				size="lg"
				position="absolute"
				top={-1}
				left={-8}
				colorScheme="green"
				variant="subtle"
				// Rotate 45deg
				transform="rotate(-20deg)"
				shadow="md"
			>
				{t('COMPLETED')}
			</Tag>
			<CardHeader>
				<Grid gridTemplateColumns="auto 1fr">
					<Box pl={1} pr={4}>
						<FcDataSheet size={60} />
					</Box>
					<Grid alignContent="center">
						<Text fontWeight={800} color="primary.500" fontSize="sm">
							{t('STEP {{number}}', { number: 2 })}
						</Text>
						<Text fontWeight={800} fontSize="xl">
							{t('Create reservation calendars')}
						</Text>
					</Grid>
				</Grid>
			</CardHeader>
			<CardBody>
				<Text>
					{t(
						'Create reservation calendars to organize meetings, appointments, events, etc. Associate your availability and that of your team to organize group meetings.'
					)}
				</Text>
			</CardBody>
			<CardFooter>
				<Button to={ROUTE_DASHBOARD_BOOKING_PAGES} as={Link} w="full" colorScheme="primary">
					{t('View reservation calendars')}
				</Button>
			</CardFooter>
		</Card>
	);
}

function Step3(props: IStepProps) {
	const { t } = useTranslation();
	return (
		<Card position="relative">
			<Tag
				display={props.isCompleted ? 'flex' : 'none'}
				size="lg"
				position="absolute"
				top={-1}
				left={-8}
				colorScheme="green"
				variant="subtle"
				// Rotate 45deg
				transform="rotate(-20deg)"
				shadow="md"
			>
				{t('COMPLETED')}
			</Tag>
			<CardHeader>
				<Grid gridTemplateColumns="auto 1fr">
					<Box pl={1} pr={4}>
						<FcLike size={60} />
					</Box>
					<Grid alignContent="center">
						<Text fontWeight={800} color="primary.500" fontSize="sm">
							{t('STEP {{number}}', { number: 3 })}
						</Text>
						<Text fontWeight={800} fontSize="xl">
							{t('Get booked')}
						</Text>
					</Grid>
				</Grid>
			</CardHeader>
			<CardBody>
				<Text>
					{t(
						'Share your reservation calendar with whoever you want and receive reservations automatically at the time that suits you best.'
					)}
				</Text>
			</CardBody>
			<CardFooter>
				<Button to={ROUTE_DASHBOARD_BOOKINGS} as={Link} w="full" colorScheme="primary">
					{t('View bookings')}
				</Button>
			</CardFooter>
		</Card>
	);
}
